import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Sparkles, MessageSquare, Users, Shield, BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import investorPortalApi from "@/lib/investorPortalApi";
import { useInvestorPortalAuth } from "@/contexts/InvestorPortalAuthContext";

const fadeSection = {
  initial: { opacity: 0, y: 8 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.1 },
};

const formatDate = (iso, lang = "de") => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "de-DE", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const Card = ({ accent = "white", title, eyebrow, children, action, testId }) => (
  <motion.section
    {...fadeSection}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className="relative rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft transition-shadow hover:shadow-card flex flex-col gap-3"
    data-testid={testId}
  >
    {eyebrow && <p className="wit-eyebrow text-raspberry">{eyebrow}</p>}
    {(title || action) && (
      <div className="flex items-start justify-between gap-3">
        {title && <h2 className="font-serif text-xl sm:text-2xl text-cocoa leading-tight">{title}</h2>}
        {action}
      </div>
    )}
    <div>{children}</div>
    {accent === "sky" && <div aria-hidden className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-skyMist" />}
    {accent === "sage" && <div aria-hidden className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-sageMint" />}
    {accent === "lavender" && <div aria-hidden className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-lavenderFog" />}
    {accent === "butter" && <div aria-hidden className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-butterCream" />}
  </motion.section>
);

export default function InvestorDashboard() {
  const { t, i18n } = useTranslation();
  const { session, refreshSession } = useInvestorPortalAuth();
  const [timeline, setTimeline] = useState([]);
  const lang = i18n.language?.startsWith("en") ? "en" : "de";

  useEffect(() => {
    let c = false;
    investorPortalApi.get("/timeline").then(({ data }) => { if (!c) setTimeline(data?.events || []); }).catch(() => {});
    return () => { c = true; };
  }, []);

  useEffect(() => { refreshSession().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const me = session?.me;
  const completeness = session?.completeness;
  const firstName = me?.personal?.first_name || "";
  const twofaEnabled = me?.security?.twofa_enabled;

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-8" data-testid="investor-portal-dashboard">
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <p className="wit-eyebrow text-raspberry mb-2" data-testid="inv-dashboard-eyebrow">
          {t("invPortal.dashboard.eyebrow")}
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cocoa leading-[1.05]" data-testid="inv-dashboard-headline">
          {t("invPortal.dashboard.greeting", { name: firstName || t("invPortal.shell.investor") })}
        </h1>
        <p className="mt-3 text-warmGrey">{t("invPortal.dashboard.sub")}</p>
      </motion.header>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Profile Completeness */}
        <Card
          accent="sky"
          eyebrow={t("invPortal.dashboard.profileEyebrow")}
          title={t("invPortal.dashboard.profileTitle", { p: completeness?.percentage ?? 0 })}
          action={
            <Link to="/portal/investor/profile"
              className="text-xs text-raspberry hover:text-coral inline-flex items-center gap-1"
              data-testid="inv-profile-card-link"
            >
              {t("invPortal.dashboard.profileEdit")} <ChevronRight size={14} />
            </Link>
          }
          testId="inv-card-profile"
        >
          <div className="w-full h-2 rounded-full bg-ivory overflow-hidden">
            <div className="h-full bg-raspberry transition-all duration-700" style={{ width: `${completeness?.percentage ?? 0}%` }} />
          </div>
          {completeness?.missing_fields?.length ? (
            <p className="text-xs text-warmGrey mt-3">
              {t("invPortal.dashboard.profileMissing")}:{" "}
              {completeness.missing_fields.slice(0, 4).map((f) => t(`invPortal.fields.${f}`)).join(" · ")}
              {completeness.missing_fields.length > 4 && " …"}
            </p>
          ) : (
            <p className="text-xs text-warmGrey mt-3">{t("invPortal.dashboard.profileComplete")}</p>
          )}
        </Card>

        {/* Investment Thesis */}
        <Card
          accent="sage"
          eyebrow={t("invPortal.dashboard.thesisEyebrow")}
          title={t("invPortal.dashboard.thesisTitle")}
          action={
            <Link to="/portal/investor/thesis"
              className="text-xs text-raspberry hover:text-coral inline-flex items-center gap-1"
              data-testid="inv-thesis-card-link"
            >
              {t("invPortal.dashboard.thesisEdit")} <ChevronRight size={14} />
            </Link>
          }
          testId="inv-card-thesis"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-sageMint flex items-center justify-center text-cocoa">
              <BookOpen size={18} />
            </div>
            <p className="text-warmGrey text-sm leading-relaxed">{t("invPortal.dashboard.thesisHint")}</p>
          </div>
        </Card>

        {/* Security */}
        <Card
          accent="lavender"
          eyebrow={t("invPortal.dashboard.securityEyebrow")}
          title={t("invPortal.dashboard.securityTitle")}
          action={
            <Link to="/portal/investor/security"
              className="text-xs text-raspberry hover:text-coral inline-flex items-center gap-1"
              data-testid="inv-security-card-link"
            >
              {t("invPortal.dashboard.securityManage")} <ChevronRight size={14} />
            </Link>
          }
          testId="inv-card-security"
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${twofaEnabled ? "bg-sageMint text-cocoa" : "bg-roseMilk text-coral"}`}>
              <Shield size={18} />
            </div>
            <p className="text-sm text-cocoa">
              {twofaEnabled ? t("invPortal.dashboard.twofaOn") : t("invPortal.dashboard.twofaOff")}
            </p>
          </div>
        </Card>

        {/* Referral */}
        <Card
          accent="butter"
          eyebrow={t("invPortal.dashboard.referralEyebrow")}
          title={t("invPortal.dashboard.referralTitle")}
          action={
            <Link to="/portal/investor/referral"
              className="text-xs text-raspberry hover:text-coral inline-flex items-center gap-1"
              data-testid="inv-referral-card-link"
            >
              {t("invPortal.dashboard.referralCta")} <ChevronRight size={14} />
            </Link>
          }
          testId="inv-card-referral"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-butterCream flex items-center justify-center text-cocoa">
              <Users size={18} />
            </div>
            <p className="text-warmGrey text-sm leading-relaxed">{t("invPortal.dashboard.referralHint")}</p>
          </div>
        </Card>
      </div>

      {/* Recent activity */}
      <Card
        eyebrow={t("invPortal.dashboard.activityEyebrow")}
        title={t("invPortal.dashboard.activityTitle")}
        action={
          <Link to="/portal/investor/timeline"
            className="text-xs text-raspberry hover:text-coral inline-flex items-center gap-1"
            data-testid="inv-activity-card-link"
          >
            {t("invPortal.dashboard.activityAll")} <ChevronRight size={14} />
          </Link>
        }
        testId="inv-card-activity"
      >
        {timeline.length === 0 ? (
          <p className="text-warmGrey text-sm">{t("invPortal.dashboard.activityEmpty")}</p>
        ) : (
          <ul className="flex flex-col gap-3 mt-1">
            {timeline.slice(0, 5).map((ev, idx) => (
              <li key={ev.id || idx} className="flex items-start gap-3" data-testid={`inv-activity-${idx}`}>
                <span className="mt-1 w-2 h-2 rounded-full bg-skyMist shrink-0" aria-hidden />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-cocoa">{t(`invPortal.events.${ev.event_type}`, { defaultValue: ev.event_type })}</p>
                  <p className="text-xs text-warmGrey">{formatDate(ev.timestamp, lang)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Phase stubs */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Card accent="sage" eyebrow={t("invPortal.phases.phase8")} title={t("invPortal.dashboard.matchesTitle")} testId="inv-card-matches-stub">
          <p className="text-warmGrey text-sm leading-relaxed">{t("invPortal.dashboard.matchesBody")}</p>
          <div className="mt-4 rounded-2xl bg-sageMint/40 p-4 flex items-center gap-3">
            <Sparkles size={16} className="text-raspberry" />
            <p className="text-xs text-cocoa">{t("invPortal.dashboard.matchesNote")}</p>
          </div>
        </Card>
        <Card accent="lavender" eyebrow={t("invPortal.phases.phase10")} title={t("invPortal.dashboard.messagesTitle")} testId="inv-card-messages-stub">
          <p className="text-warmGrey text-sm leading-relaxed">{t("invPortal.dashboard.messagesBody")}</p>
          <div className="mt-4 rounded-2xl bg-lavenderFog/40 p-4 flex items-center gap-3">
            <MessageSquare size={16} className="text-raspberry" />
            <p className="text-xs text-cocoa">{t("invPortal.dashboard.messagesNote")}</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
